import type { ApiWire } from '@/domain/shared/types';
import {
    SmoltLaksvelParameter as ApiSmoltLaksvelParameter,
    type DailyLaksvelScoreResponseDto,
    type DailyWoundsResponseDto,
    type LaksvelParameterEntryResponseDto,
    type SmoltWelfareDataResponseDto,
    type WoundsDataResponseDto,
} from '@/services/types';

import {
    orderLaksvelParameters,
    type ScoredLaksvelParameter,
    SMOLT_LAKSVEL_PARAMETERS,
    type SmoltLaksvelParameter,
} from './laksvelParameters';
import type {
    DailyLaksvelScore,
    DailyWounds,
    LaksvelParameterEntry,
    SmoltWelfareData,
    WoundsData,
} from './types';

/**
 * The welfare section as it arrives. The parameter names are the API's enum, read as strings and
 * narrowed here, so a name this client does not know is dropped instead of trusted.
 */
export type SmoltWelfareWire = Omit<ApiWire<SmoltWelfareDataResponseDto>, 'laksvelParameters'> & {
    laksvelParameters: readonly (Omit<ApiWire<LaksvelParameterEntryResponseDto>, 'parameter'> & {
        parameter: string;
    })[];
};

const API_LAKSVEL_PARAMETER: Record<SmoltLaksvelParameter, ApiSmoltLaksvelParameter> = {
    bodyWounds: ApiSmoltLaksvelParameter.bodyWounds,
    finDamage: ApiSmoltLaksvelParameter.finDamage,
    gillLid: ApiSmoltLaksvelParameter.gillLid,
    scaleLoss: ApiSmoltLaksvelParameter.scaleLoss,
    snoutWounds: ApiSmoltLaksvelParameter.snoutWounds,
    emaciation: ApiSmoltLaksvelParameter.emaciation,
    deformity: ApiSmoltLaksvelParameter.deformity,
    deformedJaw: ApiSmoltLaksvelParameter.deformedJaw,
    skinBleeding: ApiSmoltLaksvelParameter.skinBleeding,
    cataract: ApiSmoltLaksvelParameter.cataract,
    eyeDamage: ApiSmoltLaksvelParameter.eyeDamage,
    maturity: ApiSmoltLaksvelParameter.maturity,
};

/** The chosen parameters as the API takes them: in report order, without repeats. */
export function mapLaksvelParametersToApi(
    parameters: readonly SmoltLaksvelParameter[]
): ApiSmoltLaksvelParameter[] {
    return orderLaksvelParameters(parameters).map((parameter) => API_LAKSVEL_PARAMETER[parameter]);
}

function isSmoltLaksvelParameter(value: string): value is SmoltLaksvelParameter {
    return SMOLT_LAKSVEL_PARAMETERS.some((parameter) => parameter === value);
}

function isScoredLaksvelParameter(value: string): value is ScoredLaksvelParameter {
    return value !== 'bodyWounds' && isSmoltLaksvelParameter(value);
}

function mapDailyWoundsToDomain(dto: ApiWire<DailyWoundsResponseDto>): DailyWounds {
    return {
        day: dto.day,
        acute: dto.acute ?? null,
        healing: dto.healing ?? null,
    };
}

function mapWoundsDataToDomain(dto: ApiWire<WoundsDataResponseDto>): WoundsData {
    return {
        numMeasurements: dto.numMeasurements,
        acute: dto.acute ?? null,
        healing: dto.healing ?? null,
        development: dto.development.map(mapDailyWoundsToDomain),
    };
}

function mapDailyLaksvelScoreToDomain(
    dto: ApiWire<DailyLaksvelScoreResponseDto>
): DailyLaksvelScore {
    return {
        day: dto.day,
        score1: dto.score1 ?? null,
        score2: dto.score2 ?? null,
        score3: dto.score3 ?? null,
    };
}

function mapLaksvelParameterEntriesToDomain(
    dtos: SmoltWelfareWire['laksvelParameters']
): LaksvelParameterEntry[] {
    return dtos.flatMap((dto) => {
        const { parameter } = dto;
        if (!isScoredLaksvelParameter(parameter)) return [];
        return [
            {
                parameter,
                numMeasurements: dto.numMeasurements,
                score1: dto.score1 ?? null,
                score2: dto.score2 ?? null,
                score3: dto.score3 ?? null,
                development: dto.development.map(mapDailyLaksvelScoreToDomain),
            },
        ];
    });
}

function emptyParameter(parameter: ScoredLaksvelParameter): LaksvelParameterEntry {
    return {
        parameter,
        numMeasurements: 0,
        score1: null,
        score2: null,
        score3: null,
        development: [],
    };
}

/** The parameters a report was created with, from the names the API sends; unknown ones are dropped. */
export function mapIncludedParametersToDomain(raw: readonly string[]): SmoltLaksvelParameter[] {
    return orderLaksvelParameters(raw.filter(isSmoltLaksvelParameter));
}

/**
 * What a report without welfare figures reads as: a card per chosen parameter with no figures, as
 * such a report has always shown its four standard cards. It holds no measurement; the choice
 * comes from the report, not from the data.
 */
function welfareWithoutFigures(included: SmoltLaksvelParameter[]): SmoltWelfareData {
    return {
        windowEnd: null,
        includedParameters: included,
        numMeasurements: 0,
        fishWithoutWounds: null,
        totalWounds: null,
        wounds: included.includes('bodyWounds')
            ? { numMeasurements: 0, acute: null, healing: null, development: [] }
            : null,
        laksvelParameters: included.filter(isScoredLaksvelParameter).map(emptyParameter),
    };
}

/** The welfare section; `included` is the report's own choice, held whether or not it has figures. */
export function mapSmoltWelfareDataToDomain(
    dto: SmoltWelfareWire | null | undefined,
    included: SmoltLaksvelParameter[]
): SmoltWelfareData {
    if (!dto) {
        return welfareWithoutFigures(included);
    }

    return {
        windowEnd: dto.windowEnd ?? null,
        includedParameters: included,
        numMeasurements: dto.numMeasurements,
        fishWithoutWounds: dto.fishWithoutWounds ?? null,
        totalWounds: dto.totalWounds ?? null,
        wounds: dto.wounds ? mapWoundsDataToDomain(dto.wounds) : null,
        laksvelParameters: mapLaksvelParameterEntriesToDomain(dto.laksvelParameters),
    };
}
